import type { CityConfig } from "../city-template/types";

export const decaturALConfig: CityConfig = {
  city: "Decatur",
  state: "Alabama",
  stateAbbr: "AL",
  slug: "decatur-al",
  route: "/cities/decatur-al",
  title: "Emergency Passport Decatur, AL | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Decatur, AL — as fast as 24 hours. Serving Morgan County and North Alabama. A+ BBB rated registered State Dept. courier serving the Tennessee River Valley. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Decatur, AL",
  heroSubtitle: "North Alabama business trip to London with two weeks notice? Tennessee River cruise and your passport expired? Point Mallard weekend trip to Canada need fast documents? We help Decatur residents get their passports fast — as fast as 24 hours. A+ BBB rated registered State Dept. courier. Best price guarantee. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Decatur%20Alabama%20along%20the%20Tennessee%20River%20waterfront%20historic%20downtown%20skyline%20with%20the%20river%20bridges%20Point%20Mallard%20Park%20lush%20green%20landscape%20golden%20hour%20warm%20sunset%20light%20professional%20drone%20photography%20soft%20natural%20tones%20Southern%20charm&width=1800&height=700&seq=decatur-al-hero-2026&orientation=landscape",
  heroImageAlt: "Decatur Alabama Tennessee River city with historic downtown at golden hour",
  geoPosition: "34.5598;-86.9840",
  geoPlacename: "Decatur, Alabama",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30303",
    distance: "Located in downtown Atlanta — approximately 200 miles southeast of Decatur via I-65/I-20",
  },
  nearbyCitiesZips: [
    { name: "Decatur", zips: "35601, 35603" },
    { name: "Hartselle", zips: "35640" },
    { name: "Moulton", zips: "35650" },
    { name: "Priceville", zips: "35603" },
    { name: "Trinity", zips: "35673" },
    { name: "Athens", zips: "35611, 35613" },
    { name: "Madison", zips: "35756, 35757, 35758" },
    { name: "Huntsville", zips: "35801, 35802, 35803" },
    { name: "Danville", zips: "35619" },
    { name: "Falkville", zips: "35622" },
    { name: "Somerville", zips: "35670" },
    { name: "Muscle Shoals", zips: "35661, 35662" },
    { name: "Florence", zips: "35630, 35631" },
    { name: "Cullman", zips: "35055, 35056" },
    { name: "Hanceville", zips: "35077" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent North Alabama departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "Tennessee River Valley", desc: "Serving Decatur, Hartselle, Moulton, and the River City region" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Huntsville, AL", route: "/cities/huntsville" },
    { name: "Fast passport renewal in Birmingham, AL", route: "/cities/birmingham" },
    { name: "Emergency passport help in Atlanta, GA", route: "/cities/atlanta" },
    { name: "Urgent passport services in Nashville, TN", route: "/cities/nashville" },
    { name: "Same-day passport help in Montgomery, AL", route: "/cities/montgomery-al" },
    { name: "Passport courier services in Madison, AL", route: "/cities/madison-al" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Decatur remotely via secure courier. We do not have a walk-in office in Decatur — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Morgan County and North Alabama.",
  fpcOfficeAddress: "525 N Tryon St., Charlotte, NC 28202",
  fpcOfficeDistance: "Serving all of Morgan County and the Tennessee River Valley including Lawrence, Limestone, and Cullman counties",
  serviceAreaIntro: "From the historic riverfront and Point Mallard Park in Decatur to the charming railroad town of Hartselle, from the Wheeler National Wildlife Refuge to the growing communities of Priceville and Trinity, from the county seat of Moulton to the northern reaches of Morgan County — Fast Passport Center serves the entire North Alabama and Tennessee River Valley region. We regularly help travelers from Morgan County, Lawrence County, and surrounding areas get their passports fast without the 200-mile drive to Atlanta.",
  nearbyCitiesIntro: "We serve all Decatur neighborhoods and the surrounding North Alabama area including Hartselle, Moulton, Priceville, Trinity, Athens, and Madison. If you are anywhere in Morgan County, Limestone County, or the Tennessee River Valley, we have you covered.",
  howItWorksIntro: "Most Decatur travelers complete everything online and ship documents overnight. No need to drive to Atlanta or wait in line at the regional agency.",
  mistakesIntro: "Decatur travelers often face a 3+ hour drive to the nearest regional passport agency in Atlanta. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Decatur and Morgan County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Decatur? Here are the most common ones we hear from North Alabama travelers.",
  statePageRoute: "/states/alabama",
  statePageName: "Alabama",
  dmvWebsite: "https://www.alea.gov/driver-license",
  dmvLabel: "AL Law Enforcement Agency — Driver License Division",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Decatur travelers with immediate departure needs — as fast as 24 hours for urgent international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-bank-card-line",
      title: "Passport Card",
      description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean.",
      bullets: ["REAL ID compliant alternative", "Fits in your wallet", "Land/sea border crossing approved"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
    {
      icon: "ri-error-warning-line",
      title: "Damaged Passport",
      description: "Replacement for water-damaged, torn, or otherwise mutilated passports that are no longer valid for travel.",
      bullets: ["Damage assessment guidance", "DS-11 replacement process", "Supporting documentation help"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-file-copy-line",
      title: "Second Passport",
      description: "Obtain a second valid U.S. passport for frequent international travelers who need overlapping visas.",
      bullets: ["For frequent business travelers", "Overlapping visa support", "Expedited processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
    { name: "Passport Card", description: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean." },
    { name: "Damaged Passport", description: "Replacement for water-damaged, torn, or mutilated passports requiring DS-11 replacement." },
    { name: "Second Passport", description: "Second valid U.S. passport for frequent international travelers with overlapping visa needs." },
  ],
  testimonials: [
    {
      name: "James R.",
      location: "Decatur, AL",
      rating: 5,
      text: "Our London business trip was in 4 days and my passport had expired. Fast Passport Center got my renewal in 30 hours. Never left Decatur. Old State Bank to Big Ben in a week — incredible!",
      service: "Passport Renewal",
    },
    {
      name: "Shelby T.",
      location: "Hartselle, AL",
      rating: 5,
      text: "Medical emergency in Germany. Fast Passport Center got my passport in 24 hours. The dedicated agent handled everything for our family while I packed. Life-saving service for North Alabama.",
      service: "Emergency Passport",
    },
    {
      name: "Marcus P.",
      location: "Priceville, AL",
      rating: 5,
      text: "Both teens needed first passports for a school trip to France. Fast Passport Center walked us through the whole child passport process. Got them in 4 days. Amazing for Morgan County families.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "decatur-al-1",
      name: "Decatur Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "400 Well St NE",
      city: "Decatur",
      state: "AL",
      zip: "35601",
      phone: "(256) 353-7500",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Decatur",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Decatur facility. Appointments strongly recommended. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "decatur-al-2",
      name: "Morgan County Circuit Clerk",
      type: "County Circuit Clerk Passport Acceptance",
      address: "302 Lee St NE",
      city: "Decatur",
      state: "AL",
      zip: "35601",
      phone: "(256) 351-4615",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "Downtown Decatur Courthouse",
      website: "https://morgancounty-al.gov",
      websiteLabel: "Morgan County Circuit Clerk",
      notes: "Accepts passport applications at the courthouse. Good alternative to post office. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "decatur-al-3",
      name: "Hartselle Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 Cedar St NW",
      city: "Hartselle",
      state: "AL",
      zip: "35640",
      phone: "(256) 773-3333",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles south of Decatur",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Hartselle and south Morgan County. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "decatur-al-4",
      name: "Athens Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "615 S Clinton St",
      city: "Athens",
      state: "AL",
      zip: "35611",
      phone: "(256) 232-1920",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles north of Decatur",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Athens and Limestone County. Full passport services available.",
      icon: "ri-mail-line",
    },
    {
      id: "decatur-al-5",
      name: "Huntsville Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3401 Wall Triana Hwy SW",
      city: "Huntsville",
      state: "AL",
      zip: "35813",
      phone: "(256) 464-9931",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles east of Decatur",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest regional facility. Serves Huntsville and Madison County. Full passport services available.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 3.5 hour drive to Atlanta", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Decatur photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Morgan County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Decatur applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Atlanta regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many North Alabama families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Decatur travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Decatur." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Decatur address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Decatur before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Decatur for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 3.5-hour drive to Atlanta." },
    { q: "How fast can I renew my passport in Decatur?", a: "Fast Passport Center offers fast passport renewal in Decatur in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Decatur?", a: "The nearest regional passport agency to Decatur is the Atlanta Passport Agency, approximately 200 miles southeast via I-65/I-20. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Decatur without any in-person agency visit required." },
    { q: "Can I get a same-day passport appointment near Decatur?", a: "Same-day passport appointments are only available at the Atlanta Regional Passport Agency — approximately 3.5 hours from Decatur — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Morgan County and North Alabama residents." },
    { q: "Do you serve the entire Morgan County area?", a: "Absolutely. Fast Passport Center serves the entire North Alabama and Tennessee River Valley region including Decatur, Hartselle, Moulton, Priceville, Trinity, Athens, and surrounding areas. Our courier service works seamlessly from any address in Morgan County, Lawrence County, or Limestone County." },
    { q: "Can I expedite a child passport in Decatur?", a: "Yes. Fast Passport Center expedites child passports in Decatur for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk North Alabama families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Decatur with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "Do I need to drive to the Atlanta Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
  ],
  photoLocations: [
    { name: "Walmart Supercenter — Decatur", address: "2800 Spring Ave SW, Decatur, AL 35603", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Decatur", address: "1101 6th Ave NE, Decatur, AL 35601", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Decatur", address: "1208 Beltline Rd SW, Decatur, AL 35601", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart Supercenter — Hartselle", address: "1201 Highway 31 NW, Hartselle, AL 35640", hours: "Mon–Sun: 6AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "ALEA Driver License Office — Decatur",
      address: "2411 Highway 31 S, Decatur, AL 35603",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver license and REAL ID services. Appointment recommended.",
      featured: true,
    },
    {
      name: "ALEA Driver License Office — Athens",
      address: "22035 US Highway 72, Athens, AL 35611",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Limestone County. REAL ID available. Appointment recommended.",
      featured: false,
    },
    {
      name: "ALEA Driver License Office — Huntsville",
      address: "1115-D Church St NW, Huntsville, AL 35801",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Madison County. REAL ID available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HSV",
      name: "Huntsville International Airport",
      distance: "20 miles east of Decatur",
      address: "1000 Glenn Hearn Blvd SW, Huntsville, AL 35824",
      note: "Major North Alabama airport with direct flights to hubs nationwide. About 25 minutes from Decatur.",
      flightUrl: "https://www.flyhuntsville.com/",
      flightLabel: "HSV Airport",
      featured: true,
    },
    {
      code: "BHM",
      name: "Birmingham-Shuttlesworth International Airport",
      distance: "85 miles south of Decatur",
      address: "5900 Messer Airport Hwy, Birmingham, AL 35212",
      note: "Major Alabama airport with direct flights nationwide. About 1 hour 25 minutes from Decatur.",
      flightUrl: "https://www.flybirmingham.com/",
      flightLabel: "BHM Airport",
      featured: false,
    },
    {
      code: "BNA",
      name: "Nashville International Airport",
      distance: "120 miles north of Decatur",
      address: "1 Terminal Dr, Nashville, TN 37214",
      note: "Major Tennessee airport with extensive direct flights. About 2 hours from Decatur.",
      flightUrl: "https://flynashville.com/",
      flightLabel: "BNA Airport",
      featured: false,
    },
  ],
};